import type { ReactNode } from "react";

import {
  Badge,
  Box,
  Button,
  Flex,
  Reset,
  ScrollArea,
  Text,
} from "@radix-ui/themes";

import { LivePrice } from "~/v3-poc/components/LivePrice";
import { Sparkline } from "~/v3-poc/components/Sparkline";
import type { WatchlistRow } from "./WatchlistWidget.controller";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface WatchlistWidgetPresentationSlots {
  overflowMenuItems?: ReactNode;
  nameDialog?: ReactNode;
}

export type WatchlistWidgetPresentationProps =
  WatchlistWidgetPresentationSlots &
    (
      | { status: "setup"; setup: ReactNode }
      | { status: "loading" }
      | { status: "error"; onRetry: () => void }
      | {
          status: "bound";
          rows: WatchlistRow[];
          /** The add-search node is composed by the connector (header search connector). */
          addSearch: ReactNode;
          groupSymbolError?: string;
          onToggle: (symbol: string) => void;
        }
    );

type BoundProps = Extract<
  WatchlistWidgetPresentationProps,
  { status: "bound" }
>;

const UNAVAILABLE = "—";

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatPrice = (price?: number) =>
  price === undefined ? UNAVAILABLE : priceFormatter.format(price);

const formatPercent = (changePct?: number) =>
  changePct === undefined
    ? UNAVAILABLE
    : `${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%`;

// The row is a full-bleed button (no trailing control), so the selected
// highlight can sit on the button itself; `aria-pressed` is the accessible
// signal and `data-selected` on the `li` lets CSS target the whole row.

interface WatchlistRowProps {
  row: WatchlistRow;
  onToggle: (symbol: string) => void;
}

const WatchlistRowItem = ({ row, onToggle }: WatchlistRowProps) => {
  const { symbol, name, isCrypto, isSelected, price, changePct } = row;
  const changeColor =
    changePct === undefined ? "gray" : changePct >= 0 ? "green" : "red";

  return (
    <Flex
      asChild
      align="center"
      gap="2"
      width="100%"
      py="2"
      px="2"
      className="rounded-[var(--radius-2)] hover:bg-[var(--gray-a2)] data-[selected]:bg-[var(--gray-a4)]"
    >
      <li data-selected={isSelected || undefined}>
        {/* Reset, not Button: the row is a full-bleed click target with no
            button chrome, and every Button size would re-pad it. */}
        <Reset>
          <button
            type="button"
            aria-pressed={isSelected}
            onClick={() => onToggle(symbol)}
            className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]"
          >
            <Box minWidth="0">
              <Flex align="center" gap="2">
                <Text size="2" weight="bold" truncate>
                  {symbol}
                </Text>
                {isCrypto && (
                  <Badge size="1" color="purple" variant="soft" radius="full">
                    crypto
                  </Badge>
                )}
              </Flex>
              {name && (
                <Text as="p" size="1" color="gray" truncate m="0">
                  {name}
                </Text>
              )}
            </Box>

            <Box flexShrink="0">
              <Sparkline data={row.spark} />
            </Box>

            <Flex direction="column" align="end" flexShrink="0">
              <LivePrice key={price ?? "none"} tick={row.tick} size="2">
                {formatPrice(price)}
              </LivePrice>
              <Text
                size="1"
                weight="medium"
                color={changeColor}
                className="atd-numeric"
              >
                {formatPercent(changePct)}
              </Text>
            </Flex>
          </button>
        </Reset>
      </li>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Bound body
// ---------------------------------------------------------------------------

const WatchlistWidgetBoundBody = ({
  rows,
  addSearch,
  groupSymbolError,
  onToggle,
}: BoundProps) => {
  return (
    <Flex direction="column" gap="3" p="3" height="100%" className="min-h-0">
      <Box flexShrink="0">{addSearch}</Box>
      {groupSymbolError && (
        <Text role="alert" size="1" color="red">
          {groupSymbolError}
        </Text>
      )}

      {rows.length === 0 ? (
        <Flex direction="column" align="center" gap="1" px="2" py="4">
          <Text as="p" size="2" weight="medium" align="center">
            No symbols yet
          </Text>
          <Text as="p" size="1" color="gray" align="center">
            Add symbols to start building this watchlist.
          </Text>
        </Flex>
      ) : (
        <Box flexGrow="1" className="min-h-0">
          <ScrollArea type="auto" scrollbars="vertical">
            <Flex asChild direction="column" gap="1">
              <ul className="m-0 list-none p-0">
                {rows.map((row) => (
                  <WatchlistRowItem
                    key={row.symbol}
                    row={row}
                    onToggle={onToggle}
                  />
                ))}
              </ul>
            </Flex>
          </ScrollArea>
        </Box>
      )}
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

/**
 * Pure watchlist widget presentation — all data via props, no hooks.
 *
 * The `status` union mirrors the connector's bind lifecycle: unbound widgets
 * show the setup picker, a bound widget resolves through loading/error into
 * membership rows. Overflow and name-dialog slots sit outside the status
 * body so an open dialog keeps its identity across status transitions.
 */
export const WatchlistWidgetPresentation = (
  props: WatchlistWidgetPresentationProps,
) => {
  const { overflowMenuItems, nameDialog } = props;

  let body: ReactNode;
  switch (props.status) {
    case "setup":
      body = props.setup;
      break;

    case "loading":
      body = (
        <Flex align="center" justify="center" height="100%" p="5">
          <Text size="1" color="gray">
            Loading watchlist…
          </Text>
        </Flex>
      );
      break;

    case "error":
      body = (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap="3"
          height="100%"
          p="5"
          role="alert"
        >
          <Text size="1" color="gray">
            Couldn't load the watchlist.
          </Text>
          <Button size="1" onClick={() => props.onRetry()}>
            Retry
          </Button>
        </Flex>
      );
      break;

    case "bound":
      body = <WatchlistWidgetBoundBody {...props} />;
      break;
  }

  return (
    <>
      {overflowMenuItems}
      {body}
      {nameDialog}
    </>
  );
};
